import type { User } from "@supabase/supabase-js";
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getSupabaseClient } from "../lib/supabase";

const PASSWORD_RECOVERY_STORAGE_KEY = "dbvdw-password-recovery";

interface AuthState {
  user: User | null;
  loading: boolean;
  passwordRecovery: boolean;
}

interface AuthContextValue extends AuthState {
  clearPasswordRecovery: () => void;
}

function readPasswordRecoveryMarker(): boolean {
  try {
    return sessionStorage.getItem(PASSWORD_RECOVERY_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function storePasswordRecoveryMarker(active: boolean): void {
  try {
    if (active) {
      sessionStorage.setItem(PASSWORD_RECOVERY_STORAGE_KEY, "1");
    } else {
      sessionStorage.removeItem(PASSWORD_RECOVERY_STORAGE_KEY);
    }
  } catch {
    return;
  }
}

function initialAuthState(): AuthState {
  return {
    user: null,
    loading: true,
    passwordRecovery: readPasswordRecoveryMarker(),
  };
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  passwordRecovery: false,
  clearPasswordRecovery: () => undefined,
});

export function AuthProvider({ children }: PropsWithChildren) {
  const [state, setState] = useState<AuthState>(initialAuthState);

  const clearPasswordRecovery = useCallback(() => {
    storePasswordRecoveryMarker(false);
    setState((current) => ({ ...current, passwordRecovery: false }));
  }, []);

  useEffect(() => {
    let active = true;
    const client = getSupabaseClient();

    void client.auth.getSession().then(({ data }) => {
      if (active) {
        setState((current) => ({
          user: data.session?.user ?? null,
          loading: false,
          passwordRecovery: current.passwordRecovery,
        }));
      }
    }).catch((error) => {
      console.error("Auth session initialization failed", error);
      if (active) {
        setState((current) => ({ ...current, loading: false }));
      }
    });

    const { data: subscription } = client.auth.onAuthStateChange(
      (event, session) => {
        if (!active) return;
        if (event === "PASSWORD_RECOVERY") {
          storePasswordRecoveryMarker(true);
        } else if (event === "SIGNED_OUT") {
          storePasswordRecoveryMarker(false);
        }
        setState((current) => {
          let passwordRecovery = current.passwordRecovery;
          if (event === "PASSWORD_RECOVERY") passwordRecovery = true;
          if (event === "SIGNED_OUT") passwordRecovery = false;
          return {
            user: session?.user ?? null,
            loading: false,
            passwordRecovery,
          };
        });
      },
    );

    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    ...state,
    clearPasswordRecovery,
  }), [clearPasswordRecovery, state]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}
